/*
  Метрики портфеля — §3.11.2.

  Правило экрана одно и то же во всех строках: **доля показывается вместе с
  обоими числами**. «Сорок процентов» из пяти случаев и из пятисот выглядят
  одинаково, а решения за ними стоят разные.

  Там, где считать не от чего, стоит «считать не от чего», а не ноль процентов.
  Ноль обвинил бы работу, которой не было.

  08.09.2026, решение владельца: метрики стали плашками одного вида со
  счётчиками окупаемости, название набрано крупнее и жирным - раньше оно шло
  мелкими прописными подписью к цифре, и на экране, где важна сама метрика, её
  название читалось хуже её значения. Объяснение уехало под значок «i»: оно
  занимало две-три строки под каждой плашкой и превращало разбор в текст.
*/

import { cases as casesWord, casesGenitive, money, percent } from '../api/format.js'
import InfoHint from './InfoHint.jsx'

function MetricTile({ title, value, note, hint }) {
  return (
    <div className="tile">
      <div className="tile__head">
        <div className="metric__title">{title}</div>
        <InfoHint text={hint} title="Как считается" />
      </div>
      <div className="money__value">{value}</div>
      <div className="money__note">{note}</div>
    </div>
  )
}

function RatioTile({ ratio }) {
  const known = ratio.share !== null && ratio.share !== undefined
  return (
    <MetricTile
      title={ratio.title}
      value={percent(ratio.share)}
      note={known ? `${ratio.part} из ${casesGenitive(ratio.whole)}` : 'Данных для этой метрики ещё нет'}
      hint={ratio.explanation}
    />
  )
}

export default function PortfolioMetrics({ analytics }) {
  return (
    <>
      {/*
        Собственного заголовка у блока нет с 08.09.2026: он живёт во вкладке,
        которая его открывает, и второй раз повторять её название под ней -
        сообщать человеку то, что он только что нажал.
      */}
      <div className="grid">
        <RatioTile ratio={analytics.reached} />
        <RatioTile ratio={analytics.promises_kept} />
        <RatioTile ratio={analytics.orders_cancelled} />
        <MetricTile
          title="Срок от действия до денег"
          value={
            analytics.days_to_money === null
              ? 'считать не от чего'
              : `${analytics.days_to_money} дн.`
          }
          note={
            analytics.days_to_money_cases
              ? `в расчёте - ${casesWord(analytics.days_to_money_cases)}`
              : 'Ни один платёж пока не связан с вызвавшим его действием'
          }
          hint="Средний срок от вашего действия до поступления. Позволяет называть срок, а не «когда-нибудь»."
        />
      </div>

      <div className="tile">
        <div className="tile__head">
          <div className="metric__title">Собираемость по годам возникновения долга</div>
          <InfoHint
            title="Как считается"
            text="Свежий долг возвращается кратно лучше старого - это главный довод в пользу быстрой реакции. Задним числом такую разбивку не построить. «Оплачено» - по вашему учёту, включая платежи без участия Арбитра ЖКХ; плашка «Вернулось после действий Арбитр ЖКХ» на вкладке «Плашки» считает только платежи после наших действий, поэтому цифры разные."
          />
        </div>
        {analytics.cohorts.length ? (
          <div className="table-scroll">
            {/*
              Заголовки колонок обязательны: без них четыре числа в строке
              («2025», две суммы и доля) не читаются вовсе - непонятно, что
              начислено, что возвращено и от чего доля. Найдено осмотром
              07.09.2026.
            */}
            <table className="table">
              <thead>
                <tr>
                  <th>Год долга</th>
                  <th className="table__num">Начислено</th>
                  <th className="table__num">Оплачено</th>
                  <th className="table__num">Собираемость</th>
                </tr>
              </thead>
              <tbody>
                {analytics.cohorts.map((cohort) => (
                  <tr key={cohort.year}>
                    <td>{cohort.year}</td>
                    <td className="table__num" data-label="Начислено">
                      {money(cohort.charged)}
                    </td>
                    <td className="table__num" data-label="Оплачено">
                      {money(cohort.recovered)}
                    </td>
                    <td className="table__num" data-label="Собираемость">
                      {percent(cohort.share)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="muted">Начислений в базе ещё нет - считать не от чего.</div>
        )}
      </div>
    </>
  )
}
